<template>
  <span :class="badgeClass">
    <Icon v-if="icon" :name="icon" :size="iconSize" />
    <slot>{{ text }}</slot>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Icon from './Icon.vue';

export interface BadgeProps {
  text?: string;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  size?: 'small' | 'medium' | 'large';
  icon?: string;
  outline?: boolean;
}

const props = withDefaults(defineProps<BadgeProps>(), {
  variant: 'default',
  size: 'medium',
  outline: false,
});

const badgeClass = computed(() => [
  'badge',
  `badge--${props.variant}`,
  `badge--${props.size}`,
  {
    'badge--outline': props.outline,
    'badge--with-icon': props.icon,
  }
]);

const iconSize = computed(() => {
  switch (props.size) {
    case 'small': return 10;
    case 'large': return 14;
    default: return 12;
  }
});
</script>

<style scoped lang="scss">
@use '@/styles/mixins';

.badge {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  border-radius: var(--border-radius);
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  font-family: var(--vscode-font-family);
  
  &--default {
    background-color: var(--badge-default-background, var(--vscode-badge-background));
    color: var(--badge-default-foreground, var(--vscode-badge-foreground));
  }
  
  &--success {
    background-color: var(--badge-success-background, var(--vscode-testing-iconPassed, var(--vscode-terminal-ansiGreen)));
    color: var(--badge-success-foreground, var(--vscode-button-foreground, var(--vscode-editor-background)));
  }
  
  &--warning {
    background-color: var(--badge-warning-background, var(--vscode-testing-iconQueued, var(--vscode-testing-iconSkipped, var(--vscode-editorWarning-foreground))));
    color: var(--badge-warning-foreground, var(--vscode-button-foreground, var(--vscode-editor-background)));
  }
  
  &--error {
    background-color: var(--badge-error-background, var(--vscode-testing-iconFailed, var(--vscode-errorForeground)));
    color: var(--badge-error-foreground, var(--vscode-button-foreground, var(--vscode-editor-background)));
  }
  
  &--info {
    background-color: var(--badge-info-background, var(--vscode-button-secondaryBackground, var(--vscode-button-background, var(--vscode-badge-background))));
    color: var(--badge-info-foreground, var(--vscode-button-secondaryForeground, var(--vscode-button-foreground, var(--vscode-badge-foreground))));
  }
  
  &--outline {
    background-color: transparent;
    border: 1px solid;
    
    &.badge--default {
      border-color: var(--vscode-badge-background);
      color: var(--vscode-badge-background);
    }
    
    &.badge--success {
      border-color: var(--badge-success-background, var(--vscode-testing-iconPassed, var(--vscode-terminal-ansiGreen)));
      color: var(--badge-success-background, var(--vscode-testing-iconPassed, var(--vscode-terminal-ansiGreen)));
    }
    
    &.badge--warning {
      border-color: var(--badge-warning-background, var(--vscode-testing-iconQueued, var(--vscode-testing-iconSkipped, var(--vscode-editorWarning-foreground))));
      color: var(--badge-warning-background, var(--vscode-testing-iconQueued, var(--vscode-testing-iconSkipped, var(--vscode-editorWarning-foreground))));
    }
    
    &.badge--error {
      border-color: var(--badge-error-background, var(--vscode-testing-iconFailed, var(--vscode-errorForeground)));
      color: var(--badge-error-background, var(--vscode-testing-iconFailed, var(--vscode-errorForeground)));
    }
    
    &.badge--info {
      border-color: var(--badge-info-background, var(--vscode-button-secondaryBackground, var(--vscode-button-background, var(--vscode-badge-background))));
      color: var(--badge-info-background, var(--vscode-button-secondaryBackground, var(--vscode-button-background, var(--vscode-badge-background))));
    }
  }
  
  &--small {
    padding: var(--spacing-xs) var(--spacing-sm);
    font-size: 10px;
    min-height: 16px;
  }
  
  &--medium {
    padding: var(--spacing-xs) var(--spacing-md);
    font-size: 11px;
    min-height: 18px;
  }
  
  &--large {
    padding: var(--spacing-sm) var(--spacing-lg);
    font-size: 12px;
    min-height: 20px;
  }
}
</style>
